import { CardType, GameState, Player, PreparedCard } from '../types'
import { getEffectiveHero } from './heroes'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}

export const isImmuneToDamage = (player: Player, currentRound: number): boolean => {
  const isHibernating = player.effectCards.some(c => c.type === CardType.Hibernation)
  if (isHibernating) return true
  const invincibleEffect = player.effectCards.find(c => c.type === CardType.Invincible)
  if (invincibleEffect && invincibleEffect.appliedRound !== undefined) {
    const roundsSinceApplied = currentRound - invincibleEffect.appliedRound
    if (roundsSinceApplied <= 1) {
      return true
    }
  }
  return false
}

export const isShieldEffect = (type: CardType): boolean => {
  return [
    CardType.Hibernation,
    CardType.Dispel,
    CardType.Pushback,
    CardType.Defense,
    CardType.PsyBlock,
    CardType.Frost,
    CardType.Invincible,
  ].includes(type)
}

// Cards that require a player target (Attack, Heal, etc.) - used for targeting arrow
export const isTargetableEffect = (type: CardType): boolean => {
  return [
    CardType.Attack,
    CardType.Disease,
    CardType.Heal,
    CardType.InfernoGauntlet,
    CardType.Larcendagger,
    CardType.Chromabow,
    CardType.LifeDrain,
    CardType.NaturalGrip,
    CardType.EnergySword,
    CardType.FlyingScythe,
  ].includes(type)
}

// Cards that target activation chains (Counterblast, Invincible from hand)
export const isActivationChainTargetable = (type: CardType): boolean => {
  return [CardType.Counterblast, CardType.Invincible].includes(type)
}

export const getCardEffect = (preparedCard: PreparedCard) => {
  const { card, rotation } = preparedCard
  const finalRotation = (rotation % 360 + 360) % 360
  const isFlipped = finalRotation > 90 && finalRotation < 270
  const type = isFlipped && card.typeB ? card.typeB : card.type
  const value = isFlipped && card.valueB ? card.valueB : card.value
  return { type, value }
}

export const checkAndTriggerLifeCharger = (state: GameState, playerId: number): GameState => {
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]

  // Debug logging for multiplayer investigation
  console.log(`[LifeCharger] Checking for Player ${playerId}:`, {
    health: player.health,
    essence: player.essence,
    preparedCount: player.prepared.length,
    handCount: player.hand.length,
    preparedCardTypes: player.prepared.map(pc => getCardEffect(pc).type),
    handCardTypes: player.hand.map(c => c.type)
  })

  if (player.health > 0) return state

  // Check PREPARED first (night-time scenario)
  let lifeChargerIndex = player.prepared.findIndex(pc => {
    const effect = getCardEffect(pc)
    return effect.type === CardType.LifeCharger
  })
  let foundInPrepared = lifeChargerIndex !== -1
  let lifeChargerCard: any | null = null // Changed to 'any' to accommodate Card type later

  if (foundInPrepared) {
    lifeChargerCard = player.prepared[lifeChargerIndex].card
  } else {
    // Check HAND (day-time scenario - cards were unprepared at night end)
    lifeChargerIndex = player.hand.findIndex(c => c.type === CardType.LifeCharger)
    if (lifeChargerIndex !== -1) {
      lifeChargerCard = player.hand[lifeChargerIndex]
    }
  }

  if (!lifeChargerCard) {
    console.log(`[LifeCharger] Player ${playerId} has no Life Charger in prepared or hand.`)
    log('lifeCharger', `Player ${playerId} has no Life Charger.`)
    return state
  }

  // Apply Occultist discount (50% off Artifacts) — inlined to avoid circular dependency with gameLogic.ts
  const rawCost = lifeChargerCard.essenceCost || 0
  const effectiveCost = (rawCost && lifeChargerCard.subType === 'Artifact' && player.playerClass === 'OCCULTIST') ? Math.floor(rawCost / 2) : rawCost
  console.log(`[LifeCharger] Found Life Charger in ${foundInPrepared ? 'prepared' : 'hand'}. Essence: ${player.essence}, RawCost: ${rawCost}, EffectiveCost: ${effectiveCost}, Class: ${player.playerClass}`)

  if (player.essence < effectiveCost) {
    console.log(`[LifeCharger] Player ${playerId} cannot afford Life Charger (has ${player.essence}, needs ${effectiveCost}).`)
    log('lifeCharger', `Player ${playerId} cannot afford Life Charger (has ${player.essence}, needs ${effectiveCost}).`)
    return state
  }

  console.log(`[LifeCharger] Auto-triggering Life Charger for Player ${playerId}!`)
  log('lifeCharger', `Auto-triggering Life Charger for Player ${playerId}!`)

  let newState = JSON.parse(JSON.stringify(state))
  const newPlayer = newState.players[playerIndex]
  newPlayer.essence -= effectiveCost
  const healAmount = newPlayer.maxHealth - newPlayer.health
  newPlayer.health = newPlayer.maxHealth

  // Remove from the location where it was found
  if (foundInPrepared) {
    newPlayer.prepared.splice(lifeChargerIndex, 1)
    newPlayer.hand.push(lifeChargerCard) // Return to hand
  } else {
    // Already in hand - just leave it there (it was already returned from prepared)
    // No need to move it
  }

  newState.log = [...newState.log, `t:log.lifeChargerAuto|playerName=${newPlayer.name}`, `t:log.lifeChargerRestore|playerName=${newPlayer.name}|healAmount=${healAmount}`, `[REWARD] HEAL_RECEIVED:PLAYER=${playerId}:AMOUNT=${healAmount}`]
  log('lifeCharger', `Life Charger restored ${healAmount} health.`)
  return newState
}


export const applyHealthLoss = (target: Player, amount: number, source?: Player): string[] => {
  target.health = Math.max(0, target.health - amount)
  const logs: string[] = []

  // [REWARD] Log damage for AI training rewards
  if (amount > 0 && source && source.id !== target.id) {
    // Damage dealt by source to target
    logs.push(`[REWARD] DAMAGE_DEALT:PLAYER=${source.id}:AMOUNT=${amount}`)
    logs.push(`[REWARD] DAMAGE_RECEIVED:PLAYER=${target.id}:AMOUNT=${amount}`)

    const sourceEffectiveHero = getEffectiveHero(source, target)
    if (sourceEffectiveHero === 'EVELION') {
      const heal = Math.min(amount, source.maxHealth - source.health)
      if (heal > 0) {
        source.health += heal
        logs.push(`${source.name}'s Evelion drains ${heal} life from the damage dealt!`)
        logs.push(`[REWARD] HEAL_RECEIVED:PLAYER=${source.id}:AMOUNT=${heal}`)
        logs.push(`[REWARD] HERO_POWER_USED:PLAYER=${source.id}:TYPE=EVELION_LIFE_DRAIN`)
      }
    }
  }
  return logs
}
